import { createContext, useContext, useReducer } from "react";
import { LOGO_VUOTO } from "./lib/logo";

// Select dell'accordo e delle impostazioni: i dataset arrivano da accordiOptions,
// mentre accordiGetDetails invia solo il valore selezionato.
// Mappa: nome della select in accordiOptions -> nome del campo in accordiGetDetails
export const ACCORDO_SELECTS = {
  compagnia: "idCompagnia",
  stato: "stato",
  tipologia: "idTipologia",
  provenienza: "idProvenienza",
  acquistabile: "acquistabile",
  visibile: "visibile",
  fornitore: "idFornitore",
};

export const IMPOSTAZIONI_SELECTS = {
  frazionamento: "idFrazionamento",
  tipoScadenza: "idTipoScadenza",
  utenteGestione: "idUtenteGestione",
  scontiTariffe: "idScontoTariffe",
};

// Select senza un campo corrispondente nell'accordo (usate solo nelle condizioni)
const OPTION_ONLY_SELECTS = ["categorie", "operazioni", "filtri"];

// Retrocompatibilita: se l'API invia ancora { value, dataset } teniamo solo il value
const toValue = (field) =>
  field !== null && typeof field === "object" && "value" in field
    ? field.value
    : field;

// Chiave di una riga documento: i documenti nuovi (non ancora salvati) non hanno
// idUidDocumento e usano un idLocale
export const chiaveDocumento = (documento) =>
  documento.idUidDocumento || documento.idLocale;

// Chiave di una riga garanzia: le garanzie nuove non hanno idGaranzia e usano
// un idLocale
export const chiaveGaranzia = (garanzia) =>
  garanzia.idGaranzia || garanzia.idLocale;

const normalizeSelects = (data, selects) => {
  if (!data || typeof data !== "object") return data;
  const normalized = { ...data };
  Object.values(selects).forEach((campo) => {
    if (campo in normalized) normalized[campo] = toValue(normalized[campo]);
  });
  return normalized;
};

const formReducer = (state, action) => {
  switch (action.type) {
    //Controllo che setta i dati dell'API
    case "SET_DATA_API": {
      const { accordo, impostazioni, ...rest } = action.payload;
      return {
        ...state,
        ...rest,
        accordo: {
          ...state.accordo,
          ...normalizeSelects(accordo, ACCORDO_SELECTS),
        },
        impostazioni: {
          ...state.impostazioni,
          ...normalizeSelects(impostazioni, IMPOSTAZIONI_SELECTS),
        },
        // Ultimo logo salvato: serve a capire se il logo e' cambiato
        logoSalvato: impostazioni?.logo || LOGO_VUOTO,
      };
    }
    //Dataset di tutte le select (api accordiOptions). Con applyDefaults (accordo
    //nuovo) prende anche i value preselezionati restituiti dall'API
    case "SET_OPTIONS": {
      const { options, applyDefaults } = action.payload;
      const datasets = Object.fromEntries(
        Object.entries(options).map(([key, option]) => [
          key,
          Array.isArray(option?.dataset) ? option.dataset : [],
        ]),
      );
      const nextOptions = { ...state.options, ...datasets };
      // Value preselezionati, usati ad esempio per i documenti nuovi
      const optionDefaults = Object.fromEntries(
        Object.entries(options).map(([key, option]) => [key, toValue(option)]),
      );
      if (!applyDefaults)
        return { ...state, options: nextOptions, optionDefaults };

      const defaults = (selects) =>
        Object.fromEntries(
          Object.entries(selects)
            .filter(([select]) => options[select] !== undefined)
            .map(([select, campo]) => [campo, toValue(options[select])]),
        );
      return {
        ...state,
        options: nextOptions,
        optionDefaults,
        accordo: { ...state.accordo, ...defaults(ACCORDO_SELECTS) },
        impostazioni: {
          ...state.impostazioni,
          ...defaults(IMPOSTAZIONI_SELECTS),
        },
      };
    }

    //Controllo per gli Accordi
    case "set_accordo":
      return {
        ...state,
        accordo: {
          ...state.accordo,
          ...action.payload,
        },
      };
    //Controllo per le Condizioni
    case "set_condizioni":
      return {
        ...state,
        condizioni: Array.isArray(state.condizioni)
          ? action.payload
          : { ...state.condizioni, dataset: action.payload },
      };
    //Evidenzia i campi obbligatori vuoti nelle Condizioni
    case "set_condizioni_errori":
      return { ...state, condizioniErrori: action.payload };
    //Controllo per gli Utenti delle Agenzie
    case "set_utenti":
      return {
        ...state,
        agenzie: state.agenzie.map((el) =>
          el.id === action.id ? { ...el, ...action.payload } : el,
        ),
      };

    //Controllo per le Garanzie
    case "set_garanzie":
      return {
        ...state,
        garanzie: state.garanzie.map((el) =>
          chiaveGaranzia(el) === action.id ? { ...el, ...action.payload } : el,
        ),
      };
    case "delete_garanzia":
      return {
        ...state,
        garanzie: state.garanzie.filter(
          (el) => chiaveGaranzia(el) !== action.id,
        ),
      };
    case "set_new_garanzia":
      return {
        ...state,
        garanzie: [...state.garanzie, action.payload.garanzia],
      };

    //Controllo per le Impostazioni
    case "set_impostazioni":
      return {
        ...state,
        impostazioni: {
          ...state.impostazioni,
          ...action.payload,
        },
      };

    //Controlli Agenzie e Imposta tutti i dati uguali nelle agenzie
    case "set_agenzie":
      return {
        ...state,
        agenzie: state.agenzie.map((el) =>
          el.id === action.id ? { ...el, ...action.payload } : el,
        ),
      };
    case "set_all_agenzie_stato":
      return {
        ...state,
        agenzie: state.agenzie.map((agenzia) => ({
          ...agenzia,
          abilitato: action.payload, // Aggiorna stato (abilitato) per tutte le righe
        })),
      };
    case "set_all_agenzie_visibile":
      return {
        ...state,
        agenzie: state.agenzie.map((agenzia) => ({
          ...agenzia,
          visibile: action.payload, // Aggiorna visibile per tutte le righe
        })),
      };
    case "set_all_agenzie_acquistabile":
      if (action.controllo === "acquistabile") {
        return {
          ...state,
          agenzie: state.agenzie.map((agenzia) => ({
            ...agenzia,
            [action.controllo]: action.payload,
            visibile: agenzia.visibile === 0 && action.payload === 0 ? 0 : 1, // Aggiorna acquistabile per tutte le righe
          })),
        };
      }
    case "set_all_agenzie_tipoFee":
      return {
        ...state,
        agenzie: state.agenzie.map((agenzia) => ({
          ...agenzia,
          tipoFee: action.payload, // Update the "abilitato" field for all rows
        })),
      };
    case "set_all_agenzie_fee":
      return {
        ...state,
        agenzie: state.agenzie.map((agenzia) => ({
          ...agenzia,
          fee: action.payload, // Update the "abilitato" field for all rows
        })),
      };

    //Case for documenti
    //action.id = chiaveDocumento del documento
    case "set_documenti":
      return {
        ...state,
        documenti: state.documenti.some(
          (el) => chiaveDocumento(el) === action.id,
        )
          ? state.documenti.map((el) =>
              chiaveDocumento(el) === action.id
                ? { ...el, ...action.payload }
                : el,
            )
          : [...state.documenti, action.payload],
      };
    case "set_workflow":
      return {
        ...state,
        workflow: action.payload,
      };

    case "delete_documenti":
      return {
        ...state,
        documenti: state.documenti.filter(
          (el) => chiaveDocumento(el) !== action.id,
        ),
      };
  }
};

const ContextAccordo = createContext(null);

const emptyOptions = [
  ...Object.keys(ACCORDO_SELECTS),
  ...Object.keys(IMPOSTAZIONI_SELECTS),
  ...OPTION_ONLY_SELECTS,
].reduce((acc, select) => ({ ...acc, [select]: [] }), {});

const initialData = {
  tabs: [],
  dettaglioDocumento: null,
  // Dataset delle select, popolati da accordiOptions
  options: emptyOptions,
  optionDefaults: {},
  accordo: {
    nome: "",
    note: "",
    idCompagnia: "",
    stato: "",
    idTipologia: "",
    idProvenienza: "",
    acquistabile: "",
    visibile: "",
    idFornitore: "",
  },
  condizioni: [],
  condizioniErrori: false,
  documenti: [],
  agenzie: [],
  garanzie: [],
  impostazioni: {},
  logoSalvato: LOGO_VUOTO,
  workflow: {
    workflowName: "Default Workflow",
    states: [],
    transitions: [],
    roles: ["Admin", "User"],
  },
};

export const FormAccordoProvider = ({ children }) => {
  const [formState, dispatch] = useReducer(formReducer, initialData);

  return (
    <ContextAccordo.Provider value={{ formState, dispatch }}>
      {children}
    </ContextAccordo.Provider>
  );
};

// Hook to use form context
export const useFormAccordo = () => {
  const context = useContext(ContextAccordo);
  if (!context) {
    throw new Error("useForm must be used within a FormProvider");
  }
  return context;
};
